import React, { useEffect, useState } from 'react';

type HealthStatus = 'ok' | 'degraded' | 'down';

interface HealthIndicator {
  status: HealthStatus;
  message: string;
  latency: number;
  httpStatusCode?: number;
  timestamp?: string;
  errorDetails?: {
    message?: string;
    code?: string;
    httpStatus?: number;
  };
}

interface UptimeData {
  uptime: string;
  total: number;
  available?: number;
  down?: number;
  successful: number;
  degraded?: number;
  failed?: number;
}

type HealthData = Record<string, HealthIndicator>;
type UptimeMap = Record<string, UptimeData>;

const baseUrl = process.env.REACT_APP_API_URL;

export default function HealthPanel() {
  const [healthData, setHealthData] = useState<HealthData>({});
  const [uptimeData, setUptimeData] = useState<UptimeMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  useEffect(() => {
    // Fetch uptime data periodically
    const fetchUptime = async () => {
      try {
        const response = await fetch(`${baseUrl}/health-checker/uptime?hours=24`);
        const data = await response.json();
        if (data.services) {
          setUptimeData(data.services);
        }
      } catch (e) {
        console.error('Error fetching uptime data:', e);
      }
    };

    fetchUptime();
    const uptimeInterval = setInterval(fetchUptime, 60000); // Update every minute

    // SSE for real-time updates
    const eventSource = new EventSource(`${baseUrl}/health-checker`);
    console.log('baseUrl', baseUrl);
    
    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.current) {
          setHealthData(parsed.current);
        } else {
          setHealthData(parsed);
        }
        setLoading(false);
      } catch (e) {
        console.error('Error parsing SSE data:', e);
      }
    };

    eventSource.onerror = (err: Event) => {
      console.error('SSE connection error:', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
      clearInterval(uptimeInterval);
    };
  }, []);

  const getLatencyColor = (latency: number): string => {
    if (latency < 100) return '#10b981'; // green
    if (latency < 500) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const getStatusIcon = (status: HealthStatus): string => {
    switch (status) {
      case 'ok': return '🟢';
      case 'degraded': return '🟠';
      case 'down': return '🔴';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Loading service status...</p>
      </div>
    );
  }

  return (
    <>
      <div className="app-header">
        <h1>Health Monitor</h1>
        <p>Real-time monitoring • Updates every 5 seconds</p>
      </div>
      
      <div className="panel-container">
        {Object.entries(healthData).map(([apiName, apiStatus]) => {
          const uptime = uptimeData[apiName];
          return (
            <div 
              key={apiName} 
              className="card"
              onClick={() => setSelectedService(selectedService === apiName ? null : apiName)}
              style={{ cursor: 'pointer' }}
            >
              <h3>{apiName}</h3>
              
              <div className={`status ${apiStatus.status}`}>
                <span style={{ fontSize: '1.3rem' }}>
                  {getStatusIcon(apiStatus.status)}
                </span>
                <span>
                  {apiStatus.message}
                </span>
              </div>

              <div>
                <div className="metric-row">
                  <span className="metric-label">Latency</span>
                  <span className="metric-value" style={{ color: getLatencyColor(apiStatus.latency) }}>
                    {apiStatus.latency}ms
                  </span>
                </div>

                {uptime && (
                  <div className="metric-row">
                    <span className="metric-label">Uptime (24h)</span>
                    <span className="metric-value" style={{ color: parseFloat(uptime.uptime) > 99 ? '#0D5A4F' : '#8B5A00' }}>
                      {uptime.uptime}%
                    </span>
                  </div>
                )}

                {apiStatus.httpStatusCode && (
                  <div className="metric-row">
                    <span className="metric-label">HTTP Status</span>
                    <span className="metric-value">{apiStatus.httpStatusCode}</span>
                  </div>
                )}
              </div>

              {selectedService === apiName && apiStatus.errorDetails && (
                <div className="details-section" style={{ 
                  background: '#FDE8F0',
                  borderColor: '#F7B5CA'
                }}>
                  <strong>Error Details</strong>
                  <div className="details-content">
                    {apiStatus.errorDetails.message && (
                      <div>→ {apiStatus.errorDetails.message}</div>
                    )}
                    {apiStatus.errorDetails.code && (
                      <div>→ Code: {apiStatus.errorDetails.code}</div>
                    )}
                    {apiStatus.errorDetails.httpStatus && (
                      <div>→ HTTP: {apiStatus.errorDetails.httpStatus}</div>
                    )}
                  </div>
                </div>
              )}

              {/* {uptime && selectedService === apiName && !apiStatus.errorDetails && (
                <div className="details-section">
                  <strong>Estadísticas (24h) - Circuit Breaker Activo</strong>
                  <div className="details-content">
                    <div>→ Total de checks: {uptime.total}</div>
                    <div>→ Disponibles (OK + Degraded): {uptime.available || (uptime.successful + (uptime.degraded || 0))}</div>
                    <div>→ Exitosos (OK): {uptime.successful}</div>
                    {uptime.degraded !== undefined && uptime.degraded > 0 && (
                      <div style={{ color: '#8B5A00' }}>→ Degradados (1-4 fallos): {uptime.degraded}</div>
                    )}
                    {uptime.down !== undefined && uptime.down > 0 && (
                      <div style={{ color: '#A8154A' }}>→ Down (5+ fallos consecutivos): {uptime.down}</div>
                    )}
                  </div>
                  <div className="details-content" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E8E4EC' }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                      ℹ️ Solo los checks "Down" afectan el uptime. Los "Degraded" cuentan como disponibles.
                    </div>
                  </div>
                </div>
              )} */}

              {/* {selectedService === apiName && (
                <div className="click-hint">
                  Click para ocultar detalles
                </div>
              )} */}
            </div>
          );
        })}
      </div>
    </>
  );
}
